import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useTranslation } from "react-i18next";

import { Select } from "components/select/index";
import Sheet from "components/sheet/Sheet";
import { ITeeTimeTabNew } from "../NewTeeSheet";
import { ITournament } from "redux/reducers/models/tournament";
import { ILeague } from "redux/reducers/models/league";
import { StatusCode } from "api/protocols";
import { showError } from "redux/actions/ui";
import { GetEventScorecards, GetEventScorecardsCount } from "api/rpc/2024-04/facilityAdmin/event/scorecard";
import { ButtonNew } from "components/buttonNew";
import DOMPurify from "dompurify";
import { isEqualWith, isNull } from "lodash";

interface IPrintScoreCardModal {
  open: boolean;
  onCancel: () => void;
  onOk: () => void;
  teetime: ITeeTimeTabNew;
  tournament: ITournament;
  league: ILeague;
}

interface IOptions {
  logo_display: string;
}
interface IAdvancedOptions {
  showAdvancedOptions: boolean;
  options: IOptions;
}

interface ILayoutOptions {
  layoutCount: number;
  selectedLayout: number;

  //Hold loaded layouts in a dictionary, using option # as key. Key correlates to the layout option loaded (ie, scorecard_option_1 is key 1)
  layoutDictionary: IDictionary<ILayout>;
}

interface ILayout {
  layout: string;
  options: IOptions;
}

interface IDictionary<T> {
  [Key: string]: T;
}

export default function PrintScoreCardsModal(props: IPrintScoreCardModal) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);
  let loaded = false;

  const scoreCardLogoDisplay = [t("Tee On"), t("Event"), t("Facility"), t("None")];

  //Layout
  const [layout, setLayout] = useState<ILayoutOptions>({
    layoutCount: 0,
    selectedLayout: 1,
    layoutDictionary: {},
  });

  //Options object holds all additional options
  const [advancedOptions, setAdvancedOptions] = useState<IAdvancedOptions>({
    showAdvancedOptions: false,
    options: {
      logo_display: t("Tee On"),
    },
  });

  useEffect(() => {
    void getScorecardCount();
    const printCard = false;
    const singleCard = true;
    void handlePrintScorecards(printCard, singleCard);
    loaded = true;
  }, []);

  useEffect(() => {
    const selectedLayout = layout.layoutDictionary[layout?.selectedLayout];
    const optionsDoNotMatch = checkAdvancedOptions(selectedLayout?.options);

    // Intial Load || Layout Not Loaded || Advanced Options Changed
    if (!loaded && (selectedLayout === undefined || optionsDoNotMatch)) {
      const printCard = false;
      const singleCard = true;
      void handlePrintScorecards(printCard, singleCard);
    }
  }, [layout?.selectedLayout, advancedOptions?.options]);

  function checkAdvancedOptions(selectedLayoutOptions: IOptions) {
    return !isEqualWith(selectedLayoutOptions, advancedOptions.options, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  async function getScorecardCount() {
    const scorecardCount = await GetEventScorecardsCount({}, false);
    setLayout(prev => ({ ...prev, layoutCount: scorecardCount.data.count }));
  }

  async function handlePrintScorecards(print?: boolean, single: boolean) {
    const params = {
      league_id: props?.league?.id ?? null,
      tournament_id: props?.tournament?.id ?? null,
      tee_sheet_id: props.teetime?.slots[0]?.tee_sheet_id,
      facility_id: facilityStore?.facility?.id,
      start_time: props.teetime?.slots[0]?.start_time,
      single: single,
      option: layout?.selectedLayout,
      logo_display: advancedOptions?.options.logo_display,
    };

    const eventScorecardsRes = await GetEventScorecards(params, true);

    if (eventScorecardsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error printing event scorecard")));
      return;
    }

    if (print) {
      window.open().document.write(String(eventScorecardsRes.data));
    } else {
      // Update Options when loading score cards
      setLayout(prev => ({
        ...prev,
        layoutDictionary: {
          ...prev.layoutDictionary,
          [prev.selectedLayout]: {
            layout: eventScorecardsRes.data, // View
            options: {
              logo_display: advancedOptions?.options?.logo_display,
            },
          },
        },
      }));
    }
  }

  return (
    <Sheet
      open={props.open}
      title={t("Score Card Print Preview")}
      onOk={() => handlePrintScorecards(true, false)}
      onCancel={props.onCancel}
      okText={t("Print")}
    >
      <div className="mb-2">
        <Select
          label={t("Layout")}
          onChange={value => setLayout(prev => ({ ...prev, selectedLayout: value }))}
          defaultValue={1}
        >
          {/* Layout Select using count value */}
          {[...Array(layout.layoutCount)].map((_, i) => (
            <Select.Option key={i + 1} value={i + 1}>
              {t("{{ indexTitle }} {{ index }}", { indexTitle: t("Layout"), index: i + 1 })}
            </Select.Option>
          ))}
        </Select>
      </div>

      <ButtonNew
        type="tertiary"
        className="pl-0"
        onClick={() => setAdvancedOptions(prev => ({ ...prev, showAdvancedOptions: !prev.showAdvancedOptions }))}
      >
        {t("Advanced Printing Options")}
      </ButtonNew>

      {advancedOptions?.showAdvancedOptions ? (
        <Select
          label={t("Logo Display")}
          defaultValue={advancedOptions?.options.logo_display}
          onChange={(value: string) =>
            setAdvancedOptions(prev => ({ ...prev, options: { ...prev.options, logo_display: value } }))
          }
        >
          {scoreCardLogoDisplay.map(displayOption => {
            return (
              <Select.Option key={displayOption} value={displayOption}>
                {displayOption}
              </Select.Option>
            );
          })}
        </Select>
      ) : null}

      <React.Fragment>
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(layout?.layoutDictionary[layout?.selectedLayout]?.layout, { FORCE_BODY: true }),
          }}
        />
      </React.Fragment>
    </Sheet>
  );
}
